import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LoginService } from '../../../services/login.service';
import { LoginState, PhoneVerificationState } from '../../../types/login.types';
import { TipoProveedorOAuthEnum } from '../../../constants/tipo-proveedor-oauth.enum';
import { countries } from '../../../utils/mockups/countries';
import { OtpBlockComponent } from '../otp-block/otp-block.component';
import { LogoHeaderComponent } from '../shared/logo-header/logo-header.component';
import {
  StepData,
  StepIndicatorComponent,
} from '../../shared/step-indicator/step-indicator';
import {
  EmailValidationService,
  RegisterResponse,
} from '../../../services/email-validation.service';
import { TipoVerificacionEnum } from '../../../constants/tipo-verificacion.enum';
import { ModoRegistroEnum } from '../../../constants/modo-registro.enum';
import { ErrorCodeEnum } from '../../../constants/error-code.enum';
import { CheckPhoneAvailabilityResponse } from '../../../types/auth.types';
@Component({
  standalone: true,
  selector: 'app-phone-verification',
  templateUrl: './phone-verification.component.html',
  imports: [
    CommonModule,
    FormsModule,
    OtpBlockComponent,
    LogoHeaderComponent,
    StepIndicatorComponent,
  ],
})
export class PhoneVerificationComponent implements OnInit {
  currentState: PhoneVerificationState | undefined | null;

  stepData: StepData = {
    currentStep: 2,
    totalSteps: 2,
    currentStepLabel: 'Valida tu teléfono',
    nextStepLabel: 'Registro completado',
  };

  prefijoTelefono: string = '504';
  numeroTelefono: string = '';

  error: {
    message: string;
  } | null = null;

  isLoading = signal<boolean>(false);
  isValidating = signal<boolean>(false);

  countries = countries;
  selectedCountry = this.countries[0];
  showCountrySelector: boolean = false;

  otpSent = signal<boolean>(false);
  countdown = signal<number>(30);
  idVerificacionVinculacion = signal<number>(0);

  constructor(
    private loginService: LoginService,
    private emailValidationService: EmailValidationService
  ) {
    this.loginService.loginState$.subscribe((state) => {
      console.log('state', state);
      if (state.type === LoginState.phoneVerification) {
        this.currentState = state as PhoneVerificationState;
      }
    });
  }

  ngOnInit() {}

  goBack(): void {
    this.loginService.notifyLoginState({
      type: LoginState.confirmInformation,
      timestamp: new Date(),
      loginFlow: {
        name: this.currentState?.loginFlow.name || '',
        email: this.currentState?.loginFlow.email || '',
        idProveedor:
          this.currentState?.loginFlow.idProveedor ||
          TipoProveedorOAuthEnum.CREDENCIAL,
        idToken: this.currentState?.loginFlow.idToken,
        redirectUrl: this.currentState?.loginFlow.redirectUrl || '',
      },
    });
  }

  onPhoneInput(event: any) {
    this.error = null;

    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
    this.numeroTelefono = input.value;
  }

  // FIXME: Cambiar por mascara dependiendo del pais
  get isPhoneValid(): boolean {
    return this.numeroTelefono.length >= 5 && this.numeroTelefono.length <= 20;
  }

  sendOtp() {
    if (!this.isPhoneValid) {
      this.error = {
        message: 'El número de teléfono no es válido',
      };
      return;
    }

    this.otpSent.set(false);

    this.emailValidationService
      .generateOtp({
        tipoVerificacion: TipoVerificacionEnum.CREAR_CUENTA,
        destino: this.numeroTelefono,
      })
      .subscribe({
        next: (response) => {
          if (response?.success && response?.data) {
            this.otpSent.set(true);
            this.idVerificacionVinculacion.set(response?.data?.idVerificacion);
          }
        },
        error: (error) => {
          this.error = {
            message: error?.message || 'Error generando OTP',
          };
        },
      });
  }

  cambiarNumero() {
    this.numeroTelefono = '';
    this.otpSent.set(false);
    this.idVerificacionVinculacion.set(0);
  }

  crearCuenta(otpCode: string) {
    this.isLoading.set(true);

    this.emailValidationService
      .register({
        nombre: this.currentState?.loginFlow.name || '',
        email: this.currentState?.loginFlow.email || '',
        prefijoTelefono: this.prefijoTelefono || '',
        telefono: this.numeroTelefono,
        idProveedor:
          this.currentState?.loginFlow.idProveedor ||
          TipoProveedorOAuthEnum.CREDENCIAL,
        modoRegistro: ModoRegistroEnum.CREAR_NUEVA_CUENTA,
        password: this.currentState?.loginFlow.password || '',
        idToken: this.currentState?.loginFlow.idToken || '',
        idUsuarioProveedor:
          this.currentState?.loginFlow.idUsuarioProveedor || '',
        photoUrl: this.currentState?.loginFlow.photoUrl || '',
        otp: otpCode,
        idVerificacion: this.idVerificacionVinculacion(),
      })
      .subscribe({
        next: (response) => {
          this.isLoading.set(false);
          if (response?.success && response?.data) {
            const castedResponse = response.data as RegisterResponse;
            this.loginService.notifyLoginState({
              type: LoginState.finish,
              success: true,
              timestamp: new Date(),
              user: castedResponse?.user || {},
              loginFlow: {
                name: this.currentState?.loginFlow.name || '',
                email: this.currentState?.loginFlow.email || '',
                idProveedor:
                  this.currentState?.loginFlow.idProveedor ||
                  TipoProveedorOAuthEnum.CREDENCIAL,
                idToken: this.currentState?.loginFlow.idToken || '',
              },
              redirectUrl: this.currentState?.loginFlow.redirectUrl || '',
            });
          } else if (
            response?.errorCode === ErrorCodeEnum.TELEFONO_YA_REGISTRADO
          ) {
            this.loginService.notifyLoginState({
              type: LoginState.accountDetected,
              timestamp: new Date(),
              loginFlow: {
                name: this.currentState?.loginFlow.name || '',
                email: this.currentState?.loginFlow.email || '',
                idProveedor:
                  this.currentState?.loginFlow.idProveedor ||
                  TipoProveedorOAuthEnum.CREDENCIAL,
                telefono: this.numeroTelefono,
                prefijoTelefono: this.prefijoTelefono,
                idToken: this.currentState?.loginFlow.idToken || '',
                redirectUrl: this.currentState?.loginFlow.redirectUrl || '',
                idUsuarioProveedor:
                  this.currentState?.loginFlow.idUsuarioProveedor || '',
                photoUrl: this.currentState?.loginFlow.photoUrl || '',
                password: this.currentState?.loginFlow.password || '',
              },
              foundPhoneData: response?.data as CheckPhoneAvailabilityResponse,
            });
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        },
        complete: () => {
          this.isLoading.set(false);
        },
      });
  }

  selectCountry(country: any) {
    this.selectedCountry = country;
    this.prefijoTelefono = country.telefonos[0];
    this.showCountrySelector = false;
  }

  toggleCountrySelector() {
    console.log('toggleCountrySelector');
    this.showCountrySelector = !this.showCountrySelector;
  }

  trackByCountryCode(country: any): string {
    return country.codigo;
  }

  getPhoneInputClasses(hasError: boolean): string {
    const baseClasses =
      'flex-1 px-4 py-2.5 border rounded-r-lg focus:outline-none focus:ring-2 focus:border-transparent text-gray-700';
    const errorClasses = 'border-2 border-red-500 focus:ring-red-500';
    const normalClasses = 'border border-gray-300 focus:ring-appPrimary-500';

    return `${baseClasses} ${hasError ? errorClasses : normalClasses}`;
  }

  getButtonClasses(isDisabled: boolean): string {
    const baseClasses =
      'w-full py-2.5 rounded-lg font-medium text-base transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';

    if (isDisabled) {
      return `${baseClasses} bg-gray-400 text-white cursor-not-allowed flex items-center justify-center`;
    }

    return `${baseClasses} bg-allasRed text-white hover:bg-allasRed-600 focus:ring-appPrimary-500`;
  }
}
